import React, { useState, useEffect, useCallback } from 'react';
import { domainsAPI } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../Common/Modal';
import Loader from '../Common/Loader';

const DomainManager = () => {
    const { user } = useAuth();
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [newDomain, setNewDomain] = useState('');
    const [adding, setAdding] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);

    const fetchDomains = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await domainsAPI.getAll();
            setDomains(response.data.domains || []);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load domains');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    const handleAddDomain = async (e) => {
        e.preventDefault();
        if (!newDomain.trim()) return;

        try {
            setAdding(true);
            const response = await domainsAPI.create(newDomain.trim());
            setShowAddModal(false);
            setNewDomain('');
            // Show verification instructions
            setSelectedDomain(response.data.domain);
            setShowVerifyModal(true);
            fetchDomains();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to add domain');
        } finally {
            setAdding(false);
        }
    };

    const handleVerify = async (domainId) => {
        try {
            setVerifying(true);
            setVerifyResult(null);
            const response = await domainsAPI.verify(domainId);
            setVerifyResult(response.data);
            if (response.data.success) {
                fetchDomains();
            }
        } catch (err) {
            setVerifyResult({
                success: false,
                message: err.response?.data?.message || 'Verification failed',
                verification: err.response?.data?.verification
            });
        } finally {
            setVerifying(false);
        }
    };

    const handleDelete = async (domainId) => {
        if (!window.confirm('Are you sure you want to remove this domain?')) return;

        try {
            await domainsAPI.delete(domainId);
            fetchDomains();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete domain');
        }
    };

    const openVerifyModal = (domain) => {
        setSelectedDomain(domain);
        setVerifyResult(null);
        setShowVerifyModal(true);
    };

    // Only show for paid users
    if (!user?.hasPurchased) {
        return null;
    }

    if (loading) {
        return <Loader message="Loading domains..." />;
    }

    return (
        <div className="domain-manager">
            <style>{`
        .domain-manager {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .domain-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .domain-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .domain-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          margin-top: 4px;
        }

        .domain-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .domain-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .domain-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .domain-name {
          font-weight: 500;
          color: var(--text-primary);
        }

        .domain-status {
          font-size: 12px;
          padding: 4px 8px;
          border-radius: 4px;
        }

        .domain-status.verified {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .domain-status.pending {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .domain-actions {
          display: flex;
          gap: 8px;
        }

        .verification-section {
          margin-top: 16px;
          padding: 16px;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .verification-method {
          margin-bottom: 20px;
        }

        .verification-method h4 {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .verification-method p {
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .code-block {
          background: var(--bg-tertiary);
          padding: 12px;
          border-radius: 6px;
          font-family: monospace;
          font-size: 13px;
          color: var(--text-primary);
          word-break: break-all;
          border: 1px solid var(--border-color);
        }

        .verify-result {
          margin-top: 16px;
          padding: 12px;
          border-radius: 8px;
        }

        .verify-result.success {
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid #10b981;
          color: #10b981;
        }

        .verify-result.error {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid #ef4444;
          color: #ef4444;
        }

        .empty-domains {
          text-align: center;
          padding: 24px;
          color: var(--text-secondary);
        }

        .modal-buttons {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
          margin-top: 16px;
        }

        @media (max-width: 768px) {
          .domain-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .domain-actions {
            width: 100%;
            justify-content: flex-end;
          }
        }
      `}</style>

            <div className="domain-header">
                <div>
                    <h3 className="domain-title">🔐 Verified Domains</h3>
                    <p className="domain-subtitle">
                        Verify domain ownership before running load tests (required for paid plans)
                    </p>
                </div>
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAddModal(true)}
                >
                    + Add Domain
                </button>
            </div>

            {error && (
                <div className="error-message" style={{ marginBottom: '16px' }}>
                    {error}
                </div>
            )}

            {domains.length === 0 ? (
                <div className="empty-domains">
                    <p>No domains verified yet. Add a domain to start testing.</p>
                </div>
            ) : (
                <div className="domain-list">
                    {domains.map((domain) => (
                        <div key={domain._id} className="domain-item">
                            <div className="domain-info">
                                <span className="domain-name">{domain.domain}</span>
                                <span className={`domain-status ${domain.verified ? 'verified' : 'pending'}`}>
                                    {domain.verified ? '✓ Verified' : '⏳ Pending'}
                                </span>
                            </div>
                            <div className="domain-actions">
                                {!domain.verified && (
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => openVerifyModal(domain)}
                                    >
                                        Verify
                                    </button>
                                )}
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => handleDelete(domain._id)}
                                    style={{ color: '#ef4444' }}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Domain Modal */}
            <Modal
                isOpen={showAddModal}
                onClose={() => {
                    setShowAddModal(false);
                    setNewDomain('');
                }}
                title="Add Domain for Verification"
            >
                <form onSubmit={handleAddDomain}>
                    <div className="input-group">
                        <label htmlFor="domainInput">Domain Name</label>
                        <input
                            id="domainInput"
                            type="text"
                            value={newDomain}
                            onChange={(e) => setNewDomain(e.target.value)}
                            placeholder="e.g., api.example.com"
                            required
                            autoFocus
                        />
                        <small style={{ color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                            Enter the domain you want to test (e.g., api.example.com or example.com)
                        </small>
                    </div>

                    <div className="modal-buttons">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setShowAddModal(false);
                                setNewDomain('');
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={adding || !newDomain.trim()}
                        >
                            {adding ? 'Adding...' : 'Add Domain'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Verify Domain Modal */}
            <Modal
                isOpen={showVerifyModal}
                onClose={() => {
                    setShowVerifyModal(false);
                    setSelectedDomain(null);
                    setVerifyResult(null);
                }}
                title={`Verify ${selectedDomain?.domain || 'Domain'}`}
            >
                {selectedDomain && (
                    <div>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                            Choose one of the following methods to verify ownership:
                        </p>

                        <div className="verification-section">
                            <div className="verification-method">
                                <h4>Option 1: DNS TXT Record</h4>
                                <p>Add this TXT record to your domain's DNS settings:</p>
                                <div className="code-block">
                                    alt-loadtest-verify={selectedDomain.verificationToken}
                                </div>
                            </div>

                            <div className="verification-method">
                                <h4>Option 2: File Upload</h4>
                                <p>Create a file at this path on your server:</p>
                                <div className="code-block" style={{ marginBottom: '8px' }}>
                                    /.well-known/alt-loadtest.txt
                                </div>
                                <p>With this content:</p>
                                <div className="code-block">
                                    {selectedDomain.verificationToken}
                                </div>
                            </div>
                        </div>

                        {verifyResult && (
                            <div className={`verify-result ${verifyResult.success ? 'success' : 'error'}`}>
                                {verifyResult.success ? '✓ ' : '✗ '}
                                {verifyResult.message}
                            </div>
                        )}

                        <div className="modal-buttons">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setShowVerifyModal(false);
                                    setSelectedDomain(null);
                                    setVerifyResult(null);
                                }}
                            >
                                Close
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => handleVerify(selectedDomain._id)}
                                disabled={verifying}
                            >
                                {verifying ? 'Checking...' : 'Check Verification'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default DomainManager;
